import { formatAxisYear } from './historicalDate';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const MONTH_MS = 30.4375 * DAY_MS;
const YEAR_MS = 365.25 * DAY_MS;

const TICK_LEVELS = [
  { unit: 'hour', step: 1, approxMs: HOUR_MS, minLabelPx: 72 },
  { unit: 'hour', step: 3, approxMs: 3 * HOUR_MS, minLabelPx: 72 },
  { unit: 'hour', step: 6, approxMs: 6 * HOUR_MS, minLabelPx: 72 },
  { unit: 'hour', step: 12, approxMs: 12 * HOUR_MS, minLabelPx: 72 },
  { unit: 'day', step: 1, approxMs: DAY_MS, minLabelPx: 100 },
  { unit: 'day', step: 2, approxMs: 2 * DAY_MS, minLabelPx: 84 },
  { unit: 'day', step: 5, approxMs: 5 * DAY_MS, minLabelPx: 72 },
  { unit: 'day', step: 10, approxMs: 10 * DAY_MS, minLabelPx: 72 },
  { unit: 'month', step: 1, approxMs: MONTH_MS, minLabelPx: 64 },
  { unit: 'month', step: 3, approxMs: 3 * MONTH_MS, minLabelPx: 64 },
  { unit: 'month', step: 6, approxMs: 6 * MONTH_MS, minLabelPx: 64 },
  ...[1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000].map(step => ({
    unit: 'year',
    step,
    approxMs: step * YEAR_MS,
    minLabelPx: step >= 1000 ? 104 : 88
  }))
];

const createDate = (axisYear, month = 0, day = 1, hour = 0) => {
  const date = new Date(0);
  date.setFullYear(axisYear, month, day);
  date.setHours(hour, 0, 0, 0);
  return date;
};

const axisYearStartTime = (axisYear) => createDate(axisYear).getTime();

const estimateLabelWidth = (label) => {
  return (label.length * 7) + 10;
};

const getLabelAlignment = (positionPx, label, viewportWidth) => {
  const halfWidth = estimateLabelWidth(label) / 2;
  if (positionPx < halfWidth) return 'edge-start';
  if (viewportWidth - positionPx < halfWidth) return 'edge-end';
  return 'center';
};

const getTickLevel = (scale) => {
  return TICK_LEVELS.find(level => (level.approxMs * scale) >= level.minLabelPx) || TICK_LEVELS[TICK_LEVELS.length - 1];
};

const getPosition = (time, startTime, scale) => (time - startTime) * scale;

const isVisiblePosition = (positionPx, viewportWidth) => (
  positionPx >= 0 && positionPx <= viewportWidth
);

const createTick = ({ key, label = '', time, startTime, scale, viewportWidth, level, kind, boundary = false }) => {
  const positionPx = getPosition(time, startTime, scale);
  return {
    key,
    label,
    time,
    positionPx,
    labelAlign: label ? getLabelAlignment(positionPx, label, viewportWidth) : 'center',
    level,
    kind,
    boundary
  };
};

const createSpanMarker = ({ key, label, startTime: spanStartTime, endTime: spanEndTime, viewportStartTime, scale, viewportWidth, unit }) => {
  const startPx = getPosition(spanStartTime, viewportStartTime, scale);
  const endPx = getPosition(spanEndTime, viewportStartTime, scale);
  const visibleStartPx = Math.max(0, startPx);
  const visibleEndPx = Math.min(viewportWidth, endPx);
  const visibleWidthPx = visibleEndPx - visibleStartPx;

  if (visibleWidthPx <= 0) return null;
  if (visibleWidthPx < Math.min(estimateLabelWidth(label) + 24, 96)) return null;

  return {
    key,
    label,
    unit,
    startTime: spanStartTime,
    endTime: spanEndTime,
    startPx,
    endPx,
    visibleStartPx,
    visibleEndPx,
    visibleWidthPx,
    labelPx: visibleStartPx + (visibleWidthPx / 2)
  };
};

const formatMonth = (date) => (
  date.toLocaleDateString(undefined, { month: 'short' })
);

const formatDay = (date) => String(date.getDate());

const formatHour = (date) => (
  date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
);

const formatMonthContext = (date) => (
  `${formatMonth(date)} ${formatAxisYear(date.getFullYear())}`
);

const formatDayContext = (date) => {
  const day = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${day}, ${formatAxisYear(date.getFullYear())}`;
};

const formatSpanLabel = (date, unit) => {
  if (unit === 'day') return formatDayContext(date);
  if (unit === 'month') return formatMonthContext(date);
  return formatAxisYear(date.getFullYear());
};

const alignSubYearDate = (startDate, level) => {
  const date = new Date(startDate);
  date.setMinutes(0, 0, 0);

  if (level.unit === 'hour') {
    date.setHours(Math.floor(date.getHours() / level.step) * level.step, 0, 0, 0);
    if (date < startDate) date.setHours(date.getHours() + level.step);
    return date;
  }

  if (level.unit === 'day') {
    date.setHours(0, 0, 0, 0);
    if (date < startDate) date.setDate(date.getDate() + level.step);
    return date;
  }

  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  date.setMonth(Math.floor(date.getMonth() / level.step) * level.step);
  if (date < startDate) date.setMonth(date.getMonth() + level.step);
  return date;
};

const incrementSubYearDate = (date, level) => {
  const nextDate = new Date(date);

  if (level.unit === 'hour') {
    nextDate.setHours(nextDate.getHours() + level.step);
  } else if (level.unit === 'day') {
    nextDate.setDate(nextDate.getDate() + level.step);
  } else {
    nextDate.setMonth(nextDate.getMonth() + level.step);
  }

  return nextDate;
};

const getSpanUnitForLevel = (level) => {
  if (level.unit === 'hour') return 'day';
  if (level.unit === 'day') return 'month';
  if (level.unit === 'month') return 'year';
  return null;
};

const alignSpanStartDate = (date, unit) => {
  const spanDate = new Date(date);
  spanDate.setMinutes(0, 0, 0);

  if (unit === 'day') {
    spanDate.setHours(0, 0, 0, 0);
  } else if (unit === 'month') {
    spanDate.setDate(1);
    spanDate.setHours(0, 0, 0, 0);
  } else {
    spanDate.setMonth(0, 1);
    spanDate.setHours(0, 0, 0, 0);
  }

  return spanDate;
};

const incrementSpanDate = (date, unit) => {
  const nextDate = new Date(date);
  if (unit === 'day') nextDate.setDate(nextDate.getDate() + 1);
  if (unit === 'month') nextDate.setMonth(nextDate.getMonth() + 1);
  if (unit === 'year') nextDate.setFullYear(nextDate.getFullYear() + 1);
  return nextDate;
};

const generateSpanMarkers = ({ startTime, endTime, viewportWidth, scale, level }) => {
  const unit = getSpanUnitForLevel(level);
  if (!unit) return [];

  const markers = [];
  let currentDate = alignSpanStartDate(new Date(startTime), unit);

  while (currentDate.getTime() < endTime) {
    const nextDate = incrementSpanDate(currentDate, unit);
    const marker = createSpanMarker({
      key: `span-${unit}-${currentDate.getTime()}`,
      label: formatSpanLabel(currentDate, unit),
      startTime: currentDate.getTime(),
      endTime: nextDate.getTime(),
      viewportStartTime: startTime,
      scale,
      viewportWidth,
      unit
    });

    if (marker) markers.push(marker);
    currentDate = nextDate;
  }

  return markers;
};

const getSubYearLabel = (date, level) => {
  if (level.unit === 'hour') return formatHour(date);
  if (level.unit === 'day') return formatDay(date);
  return formatMonth(date);
};

const getAnchoredDaysForStep = (step) => {
  if (step === 10) return [1, 10, 20];
  if (step === 5) return [1, 5, 10, 15, 20, 25];
  if (step === 2) return [1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31];
  return Array.from({ length: 31 }, (_, index) => index + 1);
};

const isAnchoredDayForStep = (date, step) => (
  getAnchoredDaysForStep(step).includes(date.getDate())
);

const getContextLabel = (date, level, isFirstVisibleMajor) => {
  if (level.unit === 'hour') {
    if (isFirstVisibleMajor || date.getHours() === 0) return formatDayContext(date);
    return null;
  }

  if (level.unit === 'day') {
    if (isFirstVisibleMajor || date.getDate() === 1) return formatMonthContext(date);
    return null;
  }

  if (isFirstVisibleMajor || date.getMonth() === 0) {
    return formatAxisYear(date.getFullYear());
  }

  return null;
};

const isNaturalContextBoundary = (date, level) => {
  if (level.unit === 'hour') return date.getHours() === 0;
  if (level.unit === 'day') return date.getDate() === 1;
  if (level.unit === 'month') return date.getMonth() === 0;
  return false;
};

const getMinorLevel = (level) => {
  if (level.unit === 'year') return null;
  if (level.unit === 'hour') return null;
  if (level.unit === 'day' && level.step === 10) return { unit: 'day', step: 5 };
  if (level.unit === 'day' && level.step === 5) return { unit: 'day', step: 1 };
  if (level.unit === 'day' && level.step === 2) return { unit: 'day', step: 1 };
  if (level.unit === 'month' && level.step > 1) return { unit: 'month', step: 1 };
  return null;
};

const createDayTickForDate = ({ date, startTime, scale, viewportWidth, level, kind, label = '', boundary = false }) => (
  createTick({
    key: `${kind}-day-${level.step}-${date.getTime()}`,
    label,
    time: date.getTime(),
    startTime,
    scale,
    viewportWidth,
    level,
    kind,
    boundary
  })
);

const generateAnchoredDayTicks = ({ startTime, endTime, viewportWidth, scale, level, kind, excludeStep = null }) => {
  const ticks = [];
  const startDate = new Date(startTime);
  const currentDate = createDate(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());

  while (currentDate.getTime() <= endTime) {
    const positionPx = getPosition(currentDate.getTime(), startTime, scale);

    if (
      isVisiblePosition(positionPx, viewportWidth) &&
      isAnchoredDayForStep(currentDate, level.step) &&
      (!excludeStep || !isAnchoredDayForStep(currentDate, excludeStep))
    ) {
      ticks.push(createDayTickForDate({
        date: currentDate,
        startTime,
        scale,
        viewportWidth,
        level,
        kind,
        label: kind === 'major' ? getSubYearLabel(currentDate, level) : '',
        boundary: currentDate.getDate() === 1
      }));
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return ticks;
};

const generateSubYearMinorTicks = ({ startTime, endTime, viewportWidth, scale, level }) => {
  const minorLevel = getMinorLevel(level);
  if (!minorLevel) return [];

  if (level.unit === 'day') {
    return generateAnchoredDayTicks({
      startTime,
      endTime,
      viewportWidth,
      scale,
      level: minorLevel,
      kind: 'minor',
      excludeStep: level.step
    });
  }

  const minorTicks = [];
  let currentDate = alignSubYearDate(new Date(startTime), minorLevel);

  while (currentDate.getTime() <= endTime) {
    const time = currentDate.getTime();
    const positionPx = getPosition(time, startTime, scale);

    if (isVisiblePosition(positionPx, viewportWidth)) {
      minorTicks.push(createTick({
        key: `minor-${minorLevel.unit}-${minorLevel.step}-${time}`,
        time,
        startTime,
        scale,
        viewportWidth,
        level,
        kind: 'minor'
      }));
    }

    currentDate = incrementSubYearDate(currentDate, minorLevel);
  }

  return minorTicks;
};

const generateSubYearTicks = ({ startTime, endTime, viewportWidth, scale, level }) => {
  const majorTicks = [];
  const contextLabels = [];

  if (level.unit === 'day') {
    const dayTicks = generateAnchoredDayTicks({
      startTime,
      endTime,
      viewportWidth,
      scale,
      level,
      kind: 'major'
    });

    dayTicks.forEach((tick, index) => {
      const tickDate = new Date(tick.time);
      const contextLabel = getContextLabel(tickDate, level, index === 0);
      tick.boundary = isNaturalContextBoundary(tickDate, level);
      majorTicks.push(tick);

      if (contextLabel) {
        contextLabels.push(createTick({
          key: `context-day-${level.step}-${tickDate.getTime()}`,
          label: contextLabel,
          time: tickDate.getTime(),
          startTime,
          scale,
          viewportWidth,
          level,
          kind: 'context',
          boundary: true
        }));
      }
    });

    return {
      majorTicks,
      minorTicks: generateSubYearMinorTicks({ startTime, endTime, viewportWidth, scale, level }),
      contextLabels,
      spanMarkers: generateSpanMarkers({ startTime, endTime, viewportWidth, scale, level })
    };
  }

  let currentDate = alignSubYearDate(new Date(startTime), level);

  while (currentDate.getTime() <= endTime) {
    const time = currentDate.getTime();
    const positionPx = getPosition(time, startTime, scale);

    if (isVisiblePosition(positionPx, viewportWidth)) {
      const label = getSubYearLabel(currentDate, level);
      const isFirstVisibleMajor = majorTicks.length === 0;
      const contextLabel = getContextLabel(currentDate, level, isFirstVisibleMajor);

      majorTicks.push(createTick({
        key: `major-${level.unit}-${level.step}-${time}`,
        label,
        time,
        startTime,
        scale,
        viewportWidth,
        level,
        kind: 'major',
        boundary: isNaturalContextBoundary(currentDate, level)
      }));

      if (contextLabel) {
        contextLabels.push(createTick({
          key: `context-${level.unit}-${level.step}-${time}`,
          label: contextLabel,
          time,
          startTime,
          scale,
          viewportWidth,
          level,
          kind: 'context',
          boundary: true
        }));
      }
    }

    currentDate = incrementSubYearDate(currentDate, level);
  }

  return {
    majorTicks,
    minorTicks: generateSubYearMinorTicks({ startTime, endTime, viewportWidth, scale, level }),
    contextLabels,
    spanMarkers: generateSpanMarkers({ startTime, endTime, viewportWidth, scale, level })
  };
};

const firstRoundAxisYearAtOrAfter = (axisYear, step) => {
  if (step === 1) return axisYear;

  if (axisYear <= 0) {
    const historicalYear = 1 - axisYear;
    const roundedHistoricalYear = Math.floor(historicalYear / step) * step;
    return roundedHistoricalYear >= 1 ? 1 - roundedHistoricalYear : 1;
  }

  return Math.ceil(axisYear / step) * step;
};

const nextRoundAxisYear = (axisYear, step) => {
  if (step === 1) return axisYear + 1;
  if (axisYear === 0) return 1;
  if (axisYear === 1) return step;
  if (axisYear > 0) return axisYear + step;

  const nextHistoricalYear = (1 - axisYear) - step;
  return nextHistoricalYear >= step ? 1 - nextHistoricalYear : 1;
};

const getYearMinorStep = (majorStep, scale) => {
  const candidates = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000];
  const maxMinorStep = Math.max(1, Math.floor(majorStep / 2));
  return candidates.find(step => (
    step < majorStep &&
    step <= maxMinorStep &&
    (step * YEAR_MS * scale) >= 24
  )) || null;
};

const generateYearSequence = (startAxisYear, endAxisYear, step) => {
  const years = [];
  let axisYear = firstRoundAxisYearAtOrAfter(startAxisYear, step);

  while (axisYear <= endAxisYear) {
    years.push(axisYear);

    const nextAxisYear = nextRoundAxisYear(axisYear, step);
    if (nextAxisYear <= axisYear) break;
    axisYear = nextAxisYear;
  }

  return years;
};

const generateYearTicks = ({ startTime, endTime, viewportWidth, scale, level }) => {
  const startAxisYear = new Date(startTime).getFullYear();
  const endAxisYear = new Date(endTime).getFullYear();
  const majorYears = generateYearSequence(startAxisYear, endAxisYear, level.step);
  const majorYearSet = new Set(majorYears);

  const majorTicks = majorYears
    .map(axisYear => {
      const time = axisYearStartTime(axisYear);
      const positionPx = getPosition(time, startTime, scale);
      if (!isVisiblePosition(positionPx, viewportWidth)) return null;

      return createTick({
        key: `major-year-${level.step}-${axisYear}`,
        label: formatAxisYear(axisYear),
        time,
        startTime,
        scale,
        viewportWidth,
        level,
        kind: 'major',
        boundary: true
      });
    })
    .filter(Boolean);

  const minorStep = getYearMinorStep(level.step, scale);
  const minorTicks = minorStep
    ? generateYearSequence(startAxisYear, endAxisYear, minorStep)
      .filter(axisYear => !majorYearSet.has(axisYear))
      .map(axisYear => {
        const time = axisYearStartTime(axisYear);
        const positionPx = getPosition(time, startTime, scale);
        if (!isVisiblePosition(positionPx, viewportWidth)) return null;

        return createTick({
          key: `minor-year-${minorStep}-${axisYear}`,
          time,
          startTime,
          scale,
          viewportWidth,
          level,
          kind: 'minor'
        });
      })
      .filter(Boolean)
    : [];

  return {
    majorTicks,
    minorTicks,
    contextLabels: [],
    spanMarkers: []
  };
};

export const generateTimelineTicks = ({ startTime, endTime, viewportWidth, scale }) => {
  const level = getTickLevel(scale);
  const generator = level.unit === 'year' ? generateYearTicks : generateSubYearTicks;
  const ticks = generator({
    startTime,
    endTime,
    viewportWidth,
    scale,
    level
  });

  return {
    ...ticks,
    level,
    spanMarkers: ticks.spanMarkers || [],
    ticks: [...ticks.minorTicks, ...ticks.majorTicks, ...ticks.contextLabels, ...(ticks.spanMarkers || [])]
  };
};
